import type { FeatureKey, Permission } from '@domain/vocabulary';

/**
 * The navigation model.
 *
 * Data rather than JSX so it can be filtered and tested without rendering. Each
 * destination declares what it needs, and the shell shows it only when the live
 * permission matrix and feature flags both allow it — which is how turning off a
 * module removes its section from the sidebar with no redeploy, exactly as the
 * handoff describes.
 *
 * `path` doubles as the route. Keeping them in one table means a section cannot be
 * reachable by URL while hidden from the menu, which is the usual way a
 * permission-gated nav leaks.
 */

/**
 * Which live count a destination carries as a pill.
 *
 * The key rather than the number, so this table stays a pure description the
 * tests can assert on. The shell resolves the key to a count from its own
 * queries — the nav has no business fetching.
 */
export type NavBadgeKey = 'ausencias' | 'aprobaciones' | 'cuentas';

/**
 * Los seis módulos de KNPeople.
 *
 * Viven aquí y no en `vocabulary.ts` porque hoy sólo agrupan el menú. Se mudan
 * al dominio cuando la matriz de permisos pase a ser por módulo — la decisión 3
 * de `docs/KNPEOPLE.md`—, que es cuando dejarán de ser una etiqueta de la barra
 * lateral para ser algo contra lo que se autoriza.
 */
export const MODULES = [
  'people',
  'time',
  'compensation',
  'incidents',
  'communications',
  'organization',
] as const;
export type ModuleKey = (typeof MODULES)[number];

export interface NavDestination {
  readonly path: string;
  readonly label: string;
  /** The prototype renames a few entries for the superuser. */
  readonly superLabel?: string;
  /**
   * The prototype renames an entry for someone who lacks a permission — an
   * area administrator does not file absences, so the section is other
   * people's, and calling it «Mis ausencias» would misdescribe it.
   */
  readonly labelWhenLacking?: {
    readonly permission: Permission;
    readonly label: string;
  };
  /**
   * This entry has a superuser equivalent elsewhere in the menu, so for a
   * superuser it is a duplicate rather than a section.
   *
   * Only the movement log qualifies: an area's version lives under management
   * and the company-wide one under the global settings, both pointing at
   * `/bitacora`. Without this a superuser would see «Bitácora» twice.
   *
   * It replaced `hideForSuper`, which was the same field doing a second and
   * wrong job — hiding «Mis ausencias» and «Calendario» from a superuser
   * unconditionally. That check ran *before* the permission matrix, so granting
   * a superuser `solicitar` changed the matrix, changed nothing in the sidebar,
   * and left no clue why. What a role may reach is the matrix's decision now;
   * this flag only prevents a repeated row.
   */
  readonly supersededForSuper?: boolean;
  /** Entries that exist only for a superuser, in their own group. */
  readonly onlyForSuper?: boolean;
  /**
   * Sólo para quien ve más de un área.
   *
   * Distinto de `onlyForSuper`, y la diferencia es el motivo por el que existe:
   * aquello acota por **rol** —lo que configura la plataforma— y esto por
   * **alcance**. El panorama de la empresa no lo abre un permiso, porque no hay
   * ninguno que signifique «tu área no es la frontera»: eso lo dice el rol, y lo
   * dicen dos, `global` y `super`. Espejo de `seesEveryArea` en el servidor y de
   * `inScope` en las reglas.
   */
  readonly onlyCrossArea?: boolean;
  /** Unicode glyph; the design ships no icon library. */
  readonly glyph: string;
  /** Any one of these permissions grants access. */
  readonly permissions: readonly Permission[];
  /** The module this section belongs to, if any. */
  readonly feature?: FeatureKey;
  /** The count this entry shows as a pill, if any. */
  readonly badge?: NavBadgeKey;
}

export interface NavGroup {
  readonly label: string;
  /**
   * El módulo de KNPeople que este grupo representa, si es uno.
   *
   * Dos grupos no lo llevan y es a propósito: «General» e «Sistema» son
   * transversales. Inicio es el tablero, la bitácora es el registro de lo que
   * hizo el sistema y la configuración gobierna a los seis — meter cualquiera de
   * los tres dentro de un módulo diría algo falso. La bitácora en particular
   * **no es Incidencias**: ver la nota de ese módulo en `docs/KNPEOPLE.md`.
   */
  readonly module?: ModuleKey;
  readonly destinations: readonly NavDestination[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  /*
   * Los seis módulos de KNPeople como estantes, más dos grupos transversales.
   *
   * **Ésta es la primera cosa que se sale del prototipo a propósito.** Su
   * `navGroups` agrupaba por lo que el rol hace —«Mi tiempo», «Gestión del área»,
   * «Administración»— con un máximo de tres grupos. Esa forma no sabe decir dónde
   * va Compensaciones, porque nómina no es «mi tiempo» ni «gestión del área»: es
   * otro módulo. Agrupar por módulo es lo que hace visible el esquema, y por eso
   * es el paso 1 de `docs/KNPEOPLE.md` § «El orden».
   *
   * El orden es de uso, no el de la tabla del plan: la barra lateral se recorre
   * todos los días y Tiempo es el módulo que la gente abre. Los tres módulos
   * vacíos se declaran igual —el esquema tiene que verse en el dato, no sólo en
   * el documento— y `visibleNavigation` los deja fuera por no tener destinos.
   * **Un estante vacío no se muestra**, que es lo honesto: una entrada de menú
   * que no lleva a ningún lado es peor que ninguna entrada.
   *
   * Lo que se perdió al reagrupar, y conviene saber por qué: los grupos ya no se
   * renombran para el superusuario. «Mi tiempo» se volvía «Sistema» porque
   * afirmaba de quién era el tiempo y un superusuario no tiene; «Tiempo y
   * ausencias» es el nombre de un módulo y no afirma nada de nadie, así que la
   * premisa del renombre desapareció con el grupo que la sostenía. Los destinos
   * sí siguen renombrándose — Inicio es «Estado del sistema» para un super.
   */
  {
    label: 'General',
    destinations: [
      {
        /*
         * Kn·Blog: cumpleaños —y lo que se agregue— de la empresa entera.
         *
         * Primero en el menú, antes que Inicio, porque así se pidió el 1 oct 2026:
         * es lo único del portal que no es trabajo y se quiere a la vista. Sin
         * permiso —lo ve todo el que entra—, pero detrás de su módulo, `knblog`,
         * que nace apagado y se enciende en Configuración global › Features.
         */
        path: '/blog',
        label: 'Kn·Blog',
        glyph: '✦',
        permissions: [],
        feature: 'knblog',
      },
      { path: '/', label: 'Inicio', superLabel: 'Estado del sistema', glyph: '⌂', permissions: [] },
    ],
  },
  {
    label: 'Tiempo y ausencias',
    module: 'time',
    destinations: [
      {
        path: '/ausencias',
        label: 'Mis ausencias',
        glyph: '↗',
        /*
         * `solicitar` alone, and the label no longer changes.
         *
         * The section used to widen with the role and rename itself «Ausencias
         * del área», which put other people's records under «Mi tiempo» and put a
         * leader's own permit in the same list as the ones they had to decide on.
         * Deciding lives in Aprobaciones now, so what is left here is personal —
         * and someone who cannot file has nothing personal to see.
         */
        permissions: ['solicitar'],
        /*
         * The pill counts what the destination holds: the viewer's own requests,
         * permits and certificates still in flight. Other people's are counted by
         * «Aprobaciones», which is where they are now acted on.
         *
         * **Es un contador, no una notificación, y se decidió que siga siéndolo.**
         * QA reportó que «se mantiene aunque entres a la sección», y es correcto:
         * si tienes tres solicitudes en vuelo, siguen siendo tres después de
         * mirarlas. Una píldora que se apaga con trabajo pendiente miente sobre lo
         * único que tenía que decir. Decidido el 18 de agosto de 2026; si alguien
         * lo vuelve a levantar, la discusión es de diseño visual —qué tan fuerte
         * se pinta— y no de cuándo se apaga.
         */
        badge: 'ausencias',
      },
      {
        path: '/calendario',
        label: 'Calendario y feriados',
        glyph: '▦',
        permissions: ['calendario_propio', 'calendario_area'],
      },
      {
        path: '/aprobaciones',
        label: 'Aprobaciones',
        glyph: '✓',
        /*
         * `admin` as well as `aprobar`, because this is where other people's
         * absences are decided and RR.HH. decides one kind of them: a certificate
         * is validated by an administrator and by nobody else — `validateIncapacity`
         * refuses a leader outright. Without this the role whose job it is would
         * have no route to the screen.
         *
         * What each of them can act on is decided inside, by the chips.
         */
        permissions: ['aprobar', 'admin'],
        badge: 'aprobaciones',
      },
      {
        /*
         * El panorama de la empresa.
         *
         * Vive en «Personas» y no en un grupo propio porque es la misma pregunta
         * que hace el resto del módulo —quién está, quién falta, quién cubre—
         * hecha sobre las ocho áreas en vez de sobre una.
         */
        path: '/panorama',
        label: 'Panorama de la empresa',
        glyph: '◍',
        permissions: ['ver_detalle'],
        onlyCrossArea: true,
      },
      {
        path: '/reportes',
        label: 'Reportes del área',
        glyph: '≡',
        permissions: ['reportes'],
        feature: 'reportes',
      },
    ],
  },
  {
    label: 'Personas',
    module: 'people',
    destinations: [
      {
        path: '/administrador',
        label: 'Personas y cuentas',
        glyph: '★',
        /*
         * La mitad de People de lo que era «Sub áreas y cuentas»: el padrón del
         * área y la validación de cuentas pendientes. La otra mitad —sub áreas y
         * líderes— es Organization y vive en `/organizacion`.
         *
         * `areas` beside `admin`, following the split the callables already use:
         * `createSubArea` and its siblings accept either, because an area
         * administrator shapes their own area and a superuser shapes any.
         *
         * It replaced giving the superuser `admin` outright, which worked and
         * carried a passenger: `admin` is also what validates a medical
         * certificate, so the superuser inherited «Aprobaciones» and a board of
         * RR.HH.'s work under a heading that reads «Gestión del área» to somebody
         * who has none.
         */
        permissions: ['admin', 'areas'],
        badge: 'cuentas',
      },
    ],
  },
  {
    label: 'Organización',
    module: 'organization',
    destinations: [
      {
        path: '/organizacion',
        label: 'Sub áreas y líderes',
        glyph: '⌗',
        // Las mismas dos permisos que Personas: es la misma pantalla partida en
        // dos rutas, no un alcance nuevo.
        permissions: ['admin', 'areas'],
      },
      {
        path: '/areas',
        label: 'Áreas de la empresa',
        glyph: '◫',
        /*
         * Salió de «Configuración global», donde estaba por historia y no por
         * sentido: crear áreas padre y asignarles administrador es Organization.
         *
         * Al moverla se dejó del superusuario a propósito: mover una pantalla de
         * módulo no podía ampliar quién la abre, o un administrador de área
         * pasaría a crear áreas de la empresa sin que nadie lo decidiera.
         *
         * **El 28 sep 2026 sí se decidió**, y en la otra dirección: quien ve las
         * ocho áreas administra también cuáles hay. Por eso `onlyCrossArea` —
         * `global` y `super`— y no `onlyForSuper`. El administrador de área sigue
         * fuera, que era lo que aquel comentario cuidaba, y el permiso `areas`
         * sigue decidiendo: la matriz sembrada no se lo da a `global`, así que
         * quién la ve depende de la matriz viva. Espejo de `AREA_STRUCTURE_ROLES`
         * en el servidor.
         */
        onlyCrossArea: true,
        permissions: ['areas'],
      },
      {
        path: '/organigrama',
        label: 'Organigrama',
        glyph: '⊤',
        /*
         * Quién le reporta a quién, dibujado. Verlo es por rol y armarlo por
         * persona, así que la entrada sale con cualquiera de los dos: a quien sólo
         * lo arma —sin el permiso de verlo por su rol— no se le puede esconder el
         * camino a lo que se le pidió armar.
         *
         * Lleva al del área que se está mirando. Quien ve todas las áreas también
         * llega desde «Áreas de la empresa», con el área ya elegida.
         */
        permissions: ['ver_organigrama', 'editar_organigrama'],
      },
      {
        path: '/tipos-de-ausencia',
        label: 'Incapacidades y permisos',
        glyph: '⊞',
        /*
         * Del área, y por eso vive en Organización y no en Configuración global.
         *
         * Los dos catálogos eran de la empresa entera y sólo los tocaba el
         * superusuario. Cada área los reparte distinto —Customer Kare no da home
         * office como Tecnología— y quien sabe eso dirige el área. Sin
         * `onlyForSuper`, a diferencia de la vecina: aquí el administrador de
         * área es el destinatario, y `requireScope` en la callable es lo que lo
         * encierra en la suya.
         */
        permissions: ['catalogos_area'],
      },
    ],
  },
  /*
   * Comunicación deja de estar vacía: su catálogo de correos volvió a tener
   * pantalla propia en vez de vivir dentro del hub de configuración.
   *
   * Compensation e Incidents siguen sin nada, y se declaran igual para que el
   * esquema esté en el dato y no sólo en el plan. Ninguno aparece en la barra
   * hasta tener un destino.
   */
  {
    label: 'Comunicación',
    module: 'communications',
    destinations: [
      {
        path: '/correos',
        label: 'Notificaciones por correo',
        glyph: '✉',
        /*
         * El catálogo de qué aviso se manda en cada evento, a quién y con qué
         * plantilla. Era una pestaña de «Configuración global», y por eso el
         * módulo con el transporte entero funcionando —diez plantillas, la
         * extensión entregando, cuatro tareas programadas— se veía vacío.
         *
         * Mismo alcance que tenía: `config`, y sólo superusuario.
         */
        onlyForSuper: true,
        permissions: ['config'],
      },
    ],
  },
  { label: 'Compensaciones', module: 'compensation', destinations: [] },
  { label: 'Incidencias', module: 'incidents', destinations: [] },
  {
    label: 'Sistema',
    destinations: [
      {
        path: '/bitacora',
        label: 'Bitácora de movimientos',
        glyph: '§',
        permissions: ['historial'],
        feature: 'historial',
        // A superuser's log is company-wide, so it belongs with the rest of the
        // company-wide settings rather than under one area's management. Same
        // path, so this is the duplicate being suppressed, not a section denied.
        supersededForSuper: true,
      },
      {
        path: '/bitacora',
        label: 'Bitácora del sistema',
        glyph: '§',
        permissions: ['historial'],
        feature: 'historial',
        onlyForSuper: true,
      },
      /*
       * The configuration hub swallows Áreas, Feriados, Correos, Reglas, Roles,
       * Catálogos and Features. The prototype only promotes those to their own
       * entries for a role that lacks `config` — hence `cfgHub` there and the
       * single destination here.
       */
      {
        path: '/configuracion',
        label: 'Configuración global',
        glyph: '⚙',
        /*
         * The one deliberate exception to "the matrix decides".
         *
         * Everything else in this file follows the permissions and nothing else,
         * so granting a role a permission is enough to reveal its section. This
         * screen is company-wide configuration — the permission matrix itself
         * among it — and is the superuser's by definition, so it stays behind the
         * role as well as the permission. Granting `config` to an area
         * administrator would not open it.
         */
        onlyForSuper: true,
        permissions: ['config', 'features', 'areas'],
      },
    ],
  },
];

/**
 * Filters the navigation for one session.
 *
 * A destination with no declared permissions is available to anyone signed in —
 * Inicio, which has to be, because it is where a denied navigation redirects to;
 * and Kn·Blog, which its module flag gates instead. An empty group is dropped so the sidebar never shows a heading with
 * nothing under it.
 */
export const visibleNavigation = (params: {
  readonly holds: (permission: Permission) => boolean;
  readonly featureOn: (feature: FeatureKey) => boolean;
  readonly isSuper?: boolean;
  /** Si su alcance son todas las áreas: `global` y `super`. */
  readonly crossArea?: boolean;
}): readonly NavGroup[] => {
  const isSuper = params.isSuper === true;
  const crossArea = params.crossArea === true;

  return NAV_GROUPS.map((group) => {
    const destinations = group.destinations
      .filter((destination) => {
        if (isSuper && destination.supersededForSuper === true) return false;
        if (!isSuper && destination.onlyForSuper === true) return false;
        if (!crossArea && destination.onlyCrossArea === true) return false;
        if (destination.feature !== undefined && !params.featureOn(destination.feature)) {
          return false;
        }
        if (destination.permissions.length === 0) return true;
        return destination.permissions.some(params.holds);
      })
      .map((destination): NavDestination => {
        const lacking = destination.labelWhenLacking;
        const renamed =
          lacking !== undefined && !params.holds(lacking.permission) ? lacking.label : undefined;

        const { badge, ...rest } = destination;

        const label =
          (isSuper ? destination.superLabel : undefined) ?? renamed ?? destination.label;

        // Every badge now belongs to whoever can reach its destination, so the
        // destination's own `permissions` are the only gate it needs.
        return badge !== undefined ? { ...rest, label, badge } : { ...rest, label };
      });

    /*
     * El grupo ya no se renombra por rol. Un nombre de módulo no afirma de quién
     * es lo que contiene, así que no hay premisa que se pueda volver falsa — que
     * era lo que obligaba a «Mi tiempo» a volverse «Sistema». Los destinos sí se
     * renombran: ver `superLabel` arriba.
     *
     * Las dos bitácoras comparten `path`, y sólo por eso pueden convivir en el
     * mismo grupo: `supersededForSuper` y `onlyForSuper` se excluyen, así que
     * exactamente una sobrevive a este filtro. Si alguna vez las dos pasaran, la
     * barra lateral las montaría con la misma clave de React.
     */
    return group.module === undefined
      ? { label: group.label, destinations }
      : { label: group.label, module: group.module, destinations };
  }).filter((group) => group.destinations.length > 0);
};

// ---------------------------------------------------------------------------
// La barra lateral: cómo se presenta lo de arriba
// ---------------------------------------------------------------------------

/**
 * Cómo se reparten los destinos en la barra lateral.
 *
 * `NAV_GROUPS` dice **qué existe y quién lo abre**; esto dice **cómo se enseña**.
 * Son dos tablas porque dejaron de ser la misma pregunta el día que la barra de
 * un superusuario llegó a quince entradas: los seis módulos siguen siendo el
 * esquema, pero una entrada por pantalla no cabe en una laptop sin recorrerla.
 *
 * La regla: **lo de todos los días queda a un clic, y lo de administrar se
 * agrupa en secciones cuyas pantallas son pestañas.** Pedir, mirar el calendario
 * y firmar son entradas sueltas; el padrón, la estructura y la configuración
 * son tres secciones. Las rutas no cambian —cada pestaña es la pantalla de
 * siempre, con su dirección de siempre—, así que ningún marcador ni enlace de
 * correo se rompe, y `canOpen` sigue decidiendo destino por destino.
 *
 * El orden de las pestañas es de uso: la primera es a donde lleva la entrada.
 *
 * El panorama de la empresa va con «Organización» y es su primera pestaña: es
 * la vista de las áreas —quién falta en cada una, a cuál le falta líder—, y la
 * sección abre así en lo que se mira antes que en lo que se edita. Estuvo en
 * «Personas» en la primera versión y JC no lo encontró ahí; lo pidió aquí el 7
 * oct 2026.
 *
 * El organigrama va con «Personas» y no con «Organización», que es su módulo.
 * Es por quien sólo lo mira: a un líder le quedan ahí juntos el reporte de su
 * gente y el dibujo de quién le reporta a quién, en vez de una entrada suelta
 * para cada cosa.
 *
 * La bitácora tiene dos entradas con la misma ruta —la del área y la del
 * sistema— y aquí es un solo hueco: a cada quien le sobrevive una. Para el
 * superusuario es una pestaña de «Sistema»; para los demás, que de esa sección
 * no ven nada más, queda como entrada suelta con su propio nombre.
 */
interface SidebarHubSlot {
  readonly hub: string;
  readonly paths: readonly string[];
}

type SidebarSlot = string | SidebarHubSlot;

export const SIDEBAR_LAYOUT: readonly {
  readonly label: string;
  readonly slots: readonly SidebarSlot[];
}[] = [
  { label: 'General', slots: ['/blog', '/'] },
  { label: 'Tiempo y ausencias', slots: ['/ausencias', '/calendario', '/aprobaciones'] },
  {
    label: 'Gestión',
    slots: [
      { hub: 'Personas', paths: ['/administrador', '/reportes', '/organigrama'] },
      {
        hub: 'Organización',
        paths: ['/panorama', '/areas', '/organizacion', '/tipos-de-ausencia'],
      },
      { hub: 'Sistema', paths: ['/configuracion', '/correos', '/bitacora'] },
    ],
  },
];

/** Una entrada de la barra lateral: un destino, o una sección con pestañas. */
export interface SidebarEntry {
  /** El nombre de la sección; o el del destino, si de ella sólo se ve uno. */
  readonly label: string;
  /** A dónde lleva: su primera pestaña. */
  readonly path: string;
  /**
   * Sus pantallas. Con una sola es una entrada como las de siempre y no hay
   * barra de pestañas que pintar.
   */
  readonly tabs: readonly NavDestination[];
}

export interface SidebarBlock {
  readonly label: string;
  readonly entries: readonly SidebarEntry[];
}

/**
 * La barra lateral de una sesión, a partir de lo que `visibleNavigation` le deja.
 *
 * No decide permisos: recibe los destinos ya filtrados y sólo los acomoda. Una
 * sección de la que se ve una sola pantalla **no es una sección**: se enseña
 * como esa pantalla, con su nombre —un líder no tiene «Sistema», tiene «Bitácora
 * de movimientos»—. Y un destino que nadie acomodó en `SIDEBAR_LAYOUT` no se
 * pierde: sale al final, en un bloque con el nombre de su grupo. Hay una prueba
 * que impide que eso pase, y esto es lo que evita que, si pasa, alguien se quede
 * sin camino a una pantalla que sí puede abrir.
 */
export const sidebarFor = (groups: readonly NavGroup[]): readonly SidebarBlock[] => {
  const visible = new Map(
    groups.flatMap((group) => group.destinations).map((destination) => [destination.path, destination]),
  );
  const placed = new Set<string>();

  const take = (paths: readonly string[]): readonly NavDestination[] =>
    paths.flatMap((path) => {
      const destination = visible.get(path);
      if (destination === undefined) return [];
      placed.add(path);
      return [destination];
    });

  const entryOf = (hub: string | null, tabs: readonly NavDestination[]): SidebarEntry[] => {
    const first = tabs[0];
    if (first === undefined) return [];
    return [{ label: hub !== null && tabs.length > 1 ? hub : first.label, path: first.path, tabs }];
  };

  const blocks = SIDEBAR_LAYOUT.map((block) => ({
    label: block.label,
    entries: block.slots.flatMap((slot) =>
      typeof slot === 'string' ? entryOf(null, take([slot])) : entryOf(slot.hub, take(slot.paths)),
    ),
  }));

  const unplaced = groups
    .map((group) => ({
      label: group.label,
      entries: group.destinations
        .filter((destination) => !placed.has(destination.path))
        .flatMap((destination) => entryOf(null, [destination])),
    }));

  return [...blocks, ...unplaced].filter((block) => block.entries.length > 0);
};

/**
 * Si una dirección cae dentro de un destino: la suya, o una que cuelga de ella.
 *
 * El organigrama de un área es `/organigrama/<área>`, y sigue siendo «el
 * organigrama» para efectos de qué entrada se marca. La barra del final es lo
 * que impide que `/areas` se quede con `/areas-nuevas`, y que Inicio —`/`— se
 * quede con todo.
 */
export const covers = (destinationPath: string, path: string): boolean =>
  path === destinationPath || path.startsWith(`${destinationPath}/`);

/** La entrada de la barra en la que está parada una dirección, si es alguna. */
export const entryFor = (
  path: string,
  blocks: readonly SidebarBlock[],
): SidebarEntry | undefined =>
  blocks
    .flatMap((block) => block.entries)
    .find((entry) => entry.tabs.some((tab) => covers(tab.path, path)));

/**
 * Flat lookup for the breadcrumb and the access guard.
 *
 * Bitácora appears twice — once per group — and the general entry is the one to
 * resolve to. But dropping every superuser-only entry outright, which is what
 * this did, loses a path that has *no* general twin: `canOpen` would then call
 * `/configuracion` an unknown path and let anybody through, since an unknown
 * path is the router's 404 rather than a permission failure. Preferring the
 * general entry and falling back to the superuser one keeps both true.
 */
export const findDestination = (path: string): NavDestination | undefined => {
  const matches = NAV_GROUPS.flatMap((group) => group.destinations).filter(
    (destination) => destination.path === path,
  );
  return matches.find((destination) => destination.onlyForSuper !== true) ?? matches[0];
};

/**
 * Las pantallas que son un lienzo y no una página: se quedan con todo el ancho.
 *
 * El resto del portal tiene un tope de ancho a propósito —una lista o un
 * formulario de 2000 px no se lee—, y en un lienzo ese tope es justo lo que
 * estorba: en un monitor ancho dejaba el organigrama en 780 px con la mitad de la
 * pantalla vacía a su derecha. Lo reportó quien lo arma el 29 sep 2026.
 */
export const usesFullWidth = (path: string): boolean =>
  path === '/organigrama' || path.startsWith('/organigrama/');

/**
 * Whether a session may open a path.
 *
 * The same predicate the sidebar filters with, applied to a direct URL. Without
 * this, typing `/configuracion` would render a screen the menu deliberately hides —
 * the reads would fail against the security rules, but the user would see a broken
 * page instead of the handoff's clear message.
 */
export const canOpen = (
  path: string,
  params: {
    readonly holds: (permission: Permission) => boolean;
    readonly featureOn: (feature: FeatureKey) => boolean;
    readonly isSuper?: boolean;
    readonly crossArea?: boolean;
  },
): boolean => {
  const destination = findDestination(path);
  // An unknown path is not a permission failure; the router's 404 handles it.
  if (destination === undefined) return true;
  // The sidebar hides these from everyone else, so the URL has to as well.
  if (destination.onlyForSuper === true && params.isSuper !== true) return false;
  if (destination.onlyCrossArea === true && params.crossArea !== true) return false;
  if (destination.feature !== undefined && !params.featureOn(destination.feature)) return false;
  if (destination.permissions.length === 0) return true;
  return destination.permissions.some(params.holds);
};
